import { cn } from '@/utils/classname';
import { debounce } from '@/utils/debounce';
import { Input } from '@skand/ui';
import { toDigits } from '@skand/viewer-component-v2';
import { useMemo } from 'react';

const CARTOGRAPHIC_DIGITS = 9;
const HEIGHT_DIGITS = 4;
const ROTATION_DIGITS = 4;

const CARTOGRAPHIC_STEP = Math.pow(10, -CARTOGRAPHIC_DIGITS);
const HEIGHT_STEP = Math.pow(10, -HEIGHT_DIGITS);
const ROTATION_STEP = Math.pow(10, -ROTATION_DIGITS);

export interface PositionControlProps {
  longitude: number;
  latitude: number;
  height: number;
  x: number;
  y: number;
  z: number;
  setCartographicPosition: (longitude: number, latitude: number, height: number) => void;
  setRotation: (x: number, y: number, z: number) => void;
  disabled?: boolean;
}

export const PositionControl = ({
  longitude,
  latitude,
  height,
  x,
  y,
  z,
  setCartographicPosition: setCartographicPositionRaw,
  setRotation: setRotationRaw,
  disabled = false,
}: PositionControlProps) => {
  // Debounce the position update
  const setCartographicPosition = useMemo(
    () => debounce(setCartographicPositionRaw, 500),
    [setCartographicPositionRaw],
  );

  // Debounce the rotation update
  const setRotation = useMemo(() => debounce(setRotationRaw, 500), [setRotationRaw]);

  return (
    <div className="max-h-60 overflow-scroll">
      <p className={cn('typo-text-small text-neutral-800 mt-10px')}>Position</p>
      <div className={cn('mt-2')}>
        <Input
          disabled={disabled}
          label="Latitude (-90 - 90)"
          onChange={value => setCartographicPosition(longitude, parseFloat(value), height)}
          step={CARTOGRAPHIC_STEP}
          type="number"
          value={toDigits(latitude, CARTOGRAPHIC_DIGITS)}
        />
      </div>
      <div className={cn('mt-4')}>
        <Input
          disabled={disabled}
          label="Longitude (-180 - 180)"
          onChange={value => setCartographicPosition(parseFloat(value), latitude, height)}
          step={CARTOGRAPHIC_STEP}
          type="number"
          value={toDigits(longitude, CARTOGRAPHIC_DIGITS)}
        />
      </div>
      <div className={cn('mt-4')}>
        <Input
          disabled={disabled}
          label="Height (m)"
          onChange={value => setCartographicPosition(longitude, latitude, parseFloat(value))}
          step={HEIGHT_STEP}
          type="number"
          value={toDigits(height, HEIGHT_DIGITS)}
        />
      </div>

      <p className={cn('typo-text-small text-neutral-800 mt-10px')}>Rotation</p>
      <div className={cn('mt-2')}>
        <Input
          disabled={disabled}
          label="X (Degrees)"
          onChange={value => setRotation(parseFloat(value), y, z)}
          step={ROTATION_STEP}
          type="number"
          value={toDigits(x, ROTATION_DIGITS)}
        />
      </div>
      <div className={cn('mt-4')}>
        <Input
          disabled={disabled}
          label="Y (Degrees)"
          onChange={value => setRotation(x, parseFloat(value), z)}
          step={ROTATION_STEP}
          type="number"
          value={toDigits(y, ROTATION_DIGITS)}
        />
      </div>
      <div className={cn('mt-4')}>
        <Input
          disabled={disabled}
          label="Z (Degrees)"
          onChange={value => setRotation(x, y, parseFloat(value))}
          step={ROTATION_STEP}
          type="number"
          value={toDigits(z, ROTATION_DIGITS)}
        />
      </div>
    </div>
  );
};
